/**
 * Typed client for subcontractors.
 *
 * Routes (beam-backend punchlistRouter.ts):
 *   GET   /api/subcontractors            ?is_active                                  → SubcontractorRow[]
 *   POST  /api/subcontractors            { name, contact_id?, default_trade_id?, ... } → SubcontractorRow
 *   GET   /api/subcontractors/:id                                                     → SubcontractorRow
 *   PATCH /api/subcontractors/:id        { ...partial }                               → SubcontractorRow
 *   POST  /api/subcontractors/:id/users  { email, full_name, phone? }                → provisioned user row
 *
 * These endpoints return raw Prisma rows (snake_case); we type them locally rather than adding a
 * zod DTO for every shape. Bearer token via client. NEVER imports @smartbox/* (mobile invariant 1).
 */
import { get, post, patch } from './client';

export interface SubcontractorRow {
  id: string;
  name: string;
  contact_id: string | null;
  default_trade_id: string | null;
  license_number: string | null;
  insurance_expires_on: string | null;
  w9_on_file: boolean;
  is_1099: boolean;
  is_active: boolean;
  created_at: string;
}

export interface SubUserRow {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
}

export interface ListSubcontractorsParams {
  is_active?: boolean;
}

export interface CreateSubcontractorRequest {
  name: string;
  contact_id?: string;
  default_trade_id?: string;
  license_number?: string;
  insurance_expires_on?: string;
  w9_on_file?: boolean;
  is_1099?: boolean;
}

export interface UpdateSubcontractorRequest {
  name?: string;
  contact_id?: string;
  default_trade_id?: string;
  license_number?: string;
  insurance_expires_on?: string;
  w9_on_file?: boolean;
  is_1099?: boolean;
  is_active?: boolean;
}

export interface ProvisionSubUserRequest {
  email: string;
  full_name: string;
  phone?: string;
}

/** List subcontractors, optionally filtered by active flag. */
export async function listSubcontractors(
  params: ListSubcontractorsParams = {},
): Promise<SubcontractorRow[]> {
  const qs = new URLSearchParams();
  if (params.is_active !== undefined) qs.set('is_active', String(params.is_active));
  const query = qs.toString();
  return get<SubcontractorRow[]>(`/api/subcontractors${query ? '?' + query : ''}`);
}

/** Fetch a single subcontractor. */
export async function getSubcontractor(id: string): Promise<SubcontractorRow> {
  return get<SubcontractorRow>(`/api/subcontractors/${id}`);
}

/** Create a subcontractor. */
export async function createSubcontractor(
  body: CreateSubcontractorRequest,
): Promise<SubcontractorRow> {
  return post<SubcontractorRow>('/api/subcontractors', body);
}

/** Provision a portal login for the subcontractor. */
export async function provisionSubUser(
  id: string,
  body: ProvisionSubUserRequest,
): Promise<SubUserRow> {
  return post<SubUserRow>(`/api/subcontractors/${id}/users`, body);
}
